/**
 * Partner Portal Service
 * API service for partner portal (token-based authentication)
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

/**
 * Report a manual (GCash) payment for an invoice — records a claim + notifies admin.
 */
export const submitGcashPaymentClaim = async (accessToken, invoiceId, reference, amount) => {
  const fn = httpsCallable(functions, 'submitGcashPaymentClaim');
  const result = await fn({ accessToken, invoiceId, reference, amount });
  return result.data;
};

/**
 * Client proposes their own amount (flexible / "name your price" clients).
 * kind: 'monthly' (recurring) or 'devFee' (one-time development fee).
 */
export const submitMonthlyProposal = async (accessToken, amount, kind = 'monthly') => {
  const fn = httpsCallable(functions, 'submitMonthlyProposal');
  const result = await fn({ accessToken, amount, kind });
  return result.data;
};

/**
 * Single-call portal dashboard: session check + access + all data in one round trip.
 */
export const getPortalDashboard = async (sessionToken, accessToken) => {
  try {
    const fn = httpsCallable(functions, 'getPortalDashboard');
    const result = await fn({ sessionToken, accessToken });
    return result.data;
  } catch (error) {
    console.error('Error loading portal dashboard:', error);
    throw error;
  }
};

/**
 * Validate portal access token and get client data
 */
export const validatePortalAccess = async (accessToken) => {
  try {
    const validateFn = httpsCallable(functions, 'validatePortalAccess');
    const result = await validateFn({ accessToken });
    return result.data;
  } catch (error) {
    console.error('Error validating portal access:', error);
    throw error;
  }
};

/**
 * Get invoices for partner portal
 */
export const getPortalInvoices = async (accessToken) => {
  try {
    const getInvoicesFn = httpsCallable(functions, 'getPortalInvoices');
    const result = await getInvoicesFn({ accessToken });
    return result.data;
  } catch (error) {
    console.error('Error fetching portal invoices:', error);
    throw error;
  }
};

/**
 * Get invoice details for partner portal
 */
export const getPortalInvoiceDetail = async (accessToken, invoiceId) => {
  try {
    const getDetailFn = httpsCallable(functions, 'getPortalInvoiceDetail');
    const result = await getDetailFn({ accessToken, invoiceId });
    return result.data;
  } catch (error) {
    console.error('Error fetching invoice detail:', error);
    throw error;
  }
};

/**
 * Get GCP project costs breakdown for portal
 */
export const getPortalCostBreakdown = async (accessToken, billingPeriod) => {
  try {
    const getCostsFn = httpsCallable(functions, 'getPortalCostBreakdown');
    const result = await getCostsFn({ accessToken, billingPeriod });
    return result.data;
  } catch (error) {
    console.error('Error fetching cost breakdown:', error);
    throw error;
  }
};

/**
 * Create Stripe setup intent for adding payment method
 */
export const createPortalSetupIntent = async (accessToken) => {
  try {
    const createIntentFn = httpsCallable(functions, 'createPortalSetupIntent');
    const result = await createIntentFn({ accessToken });
    return result.data;
  } catch (error) {
    console.error('Error creating setup intent:', error);
    throw error;
  }
};

/**
 * Attach payment method after Stripe setup
 */
export const attachPortalPaymentMethod = async (accessToken, paymentMethodId) => {
  try {
    const attachFn = httpsCallable(functions, 'attachPortalPaymentMethod');
    const result = await attachFn({ accessToken, paymentMethodId });
    return result.data;
  } catch (error) {
    console.error('Error attaching payment method:', error);
    throw error;
  }
};

/**
 * Get payment methods for client
 */
export const getPortalPaymentMethods = async (accessToken) => {
  try {
    const getMethodsFn = httpsCallable(functions, 'getPortalPaymentMethods');
    const result = await getMethodsFn({ accessToken });
    return result.data;
  } catch (error) {
    console.error('Error fetching payment methods:', error);
    throw error;
  }
};

/**
 * Set default payment method
 */
export const setDefaultPaymentMethod = async (accessToken, paymentMethodId) => {
  try {
    const setDefaultFn = httpsCallable(functions, 'setDefaultPortalPaymentMethod');
    const result = await setDefaultFn({ accessToken, paymentMethodId });
    return result.data;
  } catch (error) {
    console.error('Error setting default payment method:', error);
    throw error;
  }
};

/**
 * Remove payment method
 */
export const removePaymentMethod = async (accessToken, paymentMethodId) => {
  try {
    const removeFn = httpsCallable(functions, 'removePortalPaymentMethod');
    const result = await removeFn({ accessToken, paymentMethodId });
    return result.data;
  } catch (error) {
    console.error('Error removing payment method:', error);
    throw error;
  }
};

/**
 * Enable/disable auto-charge for project
 */
export const updateAutoChargeSetting = async (accessToken, projectId, enabled) => {
  try {
    const updateFn = httpsCallable(functions, 'updatePortalAutoCharge');
    const result = await updateFn({ accessToken, projectId, enabled });
    return result.data;
  } catch (error) {
    console.error('Error updating auto-charge setting:', error);
    throw error;
  }
};

/**
 * Process manual payment for an invoice
 */
export const processManualPayment = async (accessToken, invoiceId, paymentMethodId) => {
  try {
    const payFn = httpsCallable(functions, 'processPortalPayment');
    const result = await payFn({ accessToken, invoiceId, paymentMethodId });
    return result.data;
  } catch (error) {
    console.error('Error processing payment:', error);
    throw error;
  }
};

/**
 * Commit the client's prepay choice (monthly / 1yr / 2yr / 3yr).
 * Server materializes the right billing artifact: subscriptions for
 * "monthly", a single up-front invoice for the prepay tiers.
 */
export const selectPrepayOption = async (accessToken, optionKey) => {
  try {
    const fn = httpsCallable(functions, 'selectPortalPrepayOption');
    const result = await fn({ accessToken, optionKey });
    return result.data;
  } catch (error) {
    console.error('Error selecting prepay option:', error);
    throw error;
  }
};

/**
 * Download invoice PDF
 */
export const getInvoicePdfUrl = async (accessToken, invoiceId) => {
  try {
    const getPdfFn = httpsCallable(functions, 'getPortalInvoicePdf');
    const result = await getPdfFn({ accessToken, invoiceId });
    return result.data;
  } catch (error) {
    console.error('Error getting invoice PDF:', error);
    throw error;
  }
};

// ============================================
// Portal Authentication
// ============================================

const SESSION_KEY = 'portal_session';

/**
 * Get stored session from localStorage
 */
export const getStoredSession = () => {
  try {
    const session = localStorage.getItem(SESSION_KEY);
    if (!session) return null;

    const parsed = JSON.parse(session);
    // Check if expired
    if (new Date(parsed.expiresAt) < new Date()) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Store session in localStorage
 */
export const storeSession = (sessionData) => {
  localStorage.setItem(SESSION_KEY, JSON.stringify(sessionData));
};

/**
 * Clear stored session
 */
export const clearSession = () => {
  localStorage.removeItem(SESSION_KEY);
};

/**
 * Send magic link email
 */
export const sendMagicLink = async (email, accessToken) => {
  try {
    const sendFn = httpsCallable(functions, 'sendPortalMagicLink');
    const result = await sendFn({ email, accessToken });
    return result.data;
  } catch (error) {
    console.error('Error sending magic link:', error);
    throw error;
  }
};

/**
 * Verify magic link token
 */
export const verifyMagicLink = async (magicToken) => {
  try {
    const verifyFn = httpsCallable(functions, 'verifyPortalMagicLink');
    const result = await verifyFn({ magicToken });
    if (result.data.success) {
      storeSession({
        sessionToken: result.data.sessionToken,
        accessToken: result.data.accessToken,
        expiresAt: result.data.expiresAt,
      });
    }
    return result.data;
  } catch (error) {
    console.error('Error verifying magic link:', error);
    throw error;
  }
};

/**
 * Send OTP email
 */
export const sendOTP = async (email, accessToken) => {
  try {
    const sendFn = httpsCallable(functions, 'sendPortalOTP');
    const result = await sendFn({ email, accessToken });
    return result.data;
  } catch (error) {
    console.error('Error sending OTP:', error);
    throw error;
  }
};

/**
 * Verify OTP code
 */
export const verifyOTP = async (email, accessToken, otp) => {
  try {
    const verifyFn = httpsCallable(functions, 'verifyPortalOTP');
    const result = await verifyFn({ email, accessToken, otp });
    if (result.data.success) {
      storeSession({
        sessionToken: result.data.sessionToken,
        accessToken: result.data.accessToken,
        expiresAt: result.data.expiresAt,
      });
    }
    return result.data;
  } catch (error) {
    console.error('Error verifying OTP:', error);
    throw error;
  }
};

/**
 * Verify session is still valid
 */
export const verifySession = async (sessionToken) => {
  try {
    const verifyFn = httpsCallable(functions, 'verifyPortalSession');
    const result = await verifyFn({ sessionToken });
    return result.data;
  } catch (error) {
    console.error('Error verifying session:', error);
    return { valid: false };
  }
};

/**
 * Sign in with Firebase Auth
 */
export const signInWithFirebase = async () => {
  try {
    const signInFn = httpsCallable(functions, 'signInWithFirebaseAuth');
    const result = await signInFn({});
    if (result.data.success) {
      storeSession({
        sessionToken: result.data.sessionToken,
        accessToken: result.data.accessToken,
        expiresAt: result.data.expiresAt,
      });
    }
    return result.data;
  } catch (error) {
    console.error('Error signing in with Firebase:', error);
    throw error;
  }
};

/**
 * Link Firebase Auth to portal
 */
export const linkFirebaseToPortal = async (accessToken) => {
  try {
    const linkFn = httpsCallable(functions, 'linkFirebaseAuthToPortal');
    const result = await linkFn({ accessToken });
    if (result.data.success) {
      storeSession({
        sessionToken: result.data.sessionToken,
        accessToken: result.data.accessToken,
        expiresAt: result.data.expiresAt,
      });
    }
    return result.data;
  } catch (error) {
    console.error('Error linking Firebase auth:', error);
    throw error;
  }
};

/**
 * Logout from portal
 */
export const portalLogout = async () => {
  const session = getStoredSession();
  if (session?.sessionToken) {
    try {
      const logoutFn = httpsCallable(functions, 'portalLogout');
      await logoutFn({ sessionToken: session.sessionToken });
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }
  clearSession();
};
